import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import AuthFrame, { authInput, planFrom } from "@/components/auth/AuthFrame";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { GreenButton } from "@/components/Buttons";
import GoogleButton from "@/components/GoogleButton";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [sp] = useSearchParams();
    const next = sp.get("next") || "";
    const light = !!next;
    const plan = planFrom(next);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const cls = authInput(light);

    const submit = async (e) => {
        e.preventDefault(); setErr(""); setBusy(true);
        try {
            const u = await login(email, password);
            const dest = next && next !== "/account" ? next : "";
            navigate(dest || (u.role === "admin" ? "/admin" : "/account"));
        } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    return (
        <AuthFrame light={light} plan={plan} next={next} title="Welcome back"
            sub={plan ? `Log in and we'll take you straight to the ${plan.name}.`
                : "Log in to track orders and reorder in one tap."}
            footer={
                <p className={`mt-6 text-sm ${light ? "text-zinc-600" : "text-center text-zinc-400"}`}>
                    New here?{" "}
                    <Link to={next ? `/register?next=${encodeURIComponent(next)}` : "/register"}
                        className={`font-semibold ${light ? "text-zinc-950 underline decoration-2" : "text-brand-green"}`}>
                        Create an account
                    </Link>
                </p>
            }>
            <div className="mt-8 space-y-4">
                <GoogleButton light={light} />
                <div className="flex items-center gap-3">
                    <span className={`h-px flex-1 ${light ? "bg-zinc-200" : "bg-white/10"}`} />
                    <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">or email</span>
                    <span className={`h-px flex-1 ${light ? "bg-zinc-200" : "bg-white/10"}`} />
                </div>
            </div>
            <form onSubmit={submit} className="mt-4 space-y-4">
                <input className={cls} type="email" placeholder="Email" value={email}
                    onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
                <input className={cls} type="password" placeholder="Password" value={password}
                    onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
                {err && <p className={`text-sm ${light ? "text-red-600" : "text-red-400"}`} data-testid="login-error">{err}</p>}
                <GreenButton className="w-full" type="submit" disabled={busy} data-testid="login-submit">
                    {busy ? <Loader2 className="animate-spin" size={16} /> : "Log In"}
                </GreenButton>
            </form>
        </AuthFrame>
    );
}
